import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, MessageCircle, Sparkles,
  Settings, Images, Package, Star, LogOut, ArrowUpRight, Key, Menu, X,
  ShoppingBag, Tag, BarChart3, KanbanSquare, MessageSquare, Boxes, MapPin,
  Monitor, Flower2, FileText, History, Database
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import AdminNotificationBell from "../../components/AdminNotificationBell";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard" },
      { to: "/admin/analytics", icon: BarChart3, label: "Analytics" },
    ]
  },
  {
    label: "POS & Inventory",
    items: [
      { to: "/admin/pos", icon: Monitor, label: "POS Terminal" },
      { to: "/admin/inventory", icon: Flower2, label: "Inventory Bunga" },
      { to: "/admin/inventory/history", icon: History, label: "Stock History" },
    ]
  },
  {
    label: "Commerce",
    items: [
      { to: "/admin/products", icon: Package, label: "Products" },
      { to: "/admin/variants", icon: Boxes, label: "Variants" },
      { to: "/admin/commerce", icon: Tag, label: "Promos & Zones" },
      { to: "/admin/orders-board", icon: KanbanSquare, label: "Orders Board" },
      { to: "/admin/reviews", icon: Star, label: "Reviews" },
    ]
  },
  {
    label: "Content (CMS)",
    items: [
      { to: "/admin/cms", icon: FileText, label: "CMS Overview" },
      { to: "/admin/landing", icon: Sparkles, label: "Landing Hero" },
      { to: "/admin/memory", icon: MessageCircle, label: "Memory AI" },
      { to: "/admin/about", icon: Star, label: "About Page" },
      { to: "/admin/settings", icon: Settings, label: "Site Settings" },
      { to: "/admin/media", icon: Images, label: "Media Library" },
    ]
  },
  {
    label: "System",
    items: [
      { to: "/admin/api-keys", icon: Key, label: "API Keys" },
      { to: "/admin/backup", icon: Database, label: "Backup & Restore" },
      { to: "/admin/legacy", icon: ShoppingBag, label: "Legacy Tools" },
    ]
  }
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { replace: true });
    } else if (user.role !== "super_admin" && user.role !== "florist_admin") {
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (loading || !user || (user.role !== "super_admin" && user.role !== "florist_admin")) {
    return (
      <div className="light-cms min-h-screen flex items-center justify-center">
        <p className="font-heading text-2xl text-[#8A857D] italic">Verifying access...</p>
      </div>
    );
  }

  const currentLabel = () => {
    const seg = location.pathname.replace("/admin/", "").replace("/admin", "") || "dashboard";
    const flat = NAV_GROUPS.flatMap(g => g.items);
    const found = flat.find(i => i.to === location.pathname || i.to === `/admin/${seg}`);
    return found?.label || seg;
  };

  const Sidebar = ({ inDrawer = false }) => (
    <aside className={`${inDrawer ? "flex" : "hidden lg:flex"} w-64 flex-col fixed left-0 top-0 bottom-0 z-40 border-r border-[#D4859B]/12 bg-white/80 backdrop-blur-xl shadow-[0_8px_32px_-12px_rgba(60,40,50,0.08)]`}>
      <div className="px-6 py-6 border-b border-[#D4859B]/12 flex items-center justify-between">
        <NavLink to="/" data-testid="admin-back-to-site" className="block">
          <span className="font-heading text-2xl text-[#1C1A19]">Seikat<span className="text-seikat-pink">.</span></span>
          <span className="block eyebrow-immersive mt-1.5 text-seikat-pink">Atelier Workspace</span>
        </NavLink>
        {inDrawer && (
          <button onClick={() => setMobileOpen(false)} aria-label="Close" className="w-8 h-8 rounded-full hover:bg-seikat-pink/10 flex items-center justify-center text-[#1C1A19]">
            <X size={15} />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-6" data-testid="admin-sidebar">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <div className="px-3 mb-1.5 text-[9px] tracking-[0.35em] uppercase text-[#C4B8AF] font-body">{group.label}</div>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13px] font-body transition-colors ${
                      isActive
                        ? "bg-seikat-pink text-white shadow-[0_4px_14px_-4px_rgba(212,133,155,0.45)]"
                        : "text-[#4A453F] hover:text-[#1C1A19] hover:bg-seikat-pink/8"
                    }`
                  }
                >
                  <item.icon size={14} strokeWidth={1.6} />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="px-3 py-4 border-t border-[#D4859B]/12 space-y-2">
        <div className="px-3 py-2.5 rounded-xl bg-seikat-pink/8 border border-seikat-pink/15">
          <p className="font-body text-xs text-[#1C1A19] truncate font-medium">{user.name || user.email}</p>
          <p className="font-body text-[10px] tracking-widest uppercase text-seikat-pink mt-0.5">{user.role.replace(/_/g, " ")}</p>
        </div>
        <button
          onClick={async () => { await logout(); navigate("/"); }}
          data-testid="admin-logout"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm text-[#8A857D] hover:text-seikat-pink hover:bg-seikat-pink/8 transition-colors"
        >
          <LogOut size={13} /> Sign Out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="light-cms min-h-screen flex" data-testid="admin-layout">
      <Sidebar />

      {mobileOpen && (
        <>
          <div onClick={() => setMobileOpen(false)} className="fixed inset-0 z-30 bg-[#1C1A19]/40 backdrop-blur-sm lg:hidden" />
          <Sidebar inDrawer />
        </>
      )}

      <header className="lg:hidden fixed top-0 left-0 right-0 z-30 flex items-center justify-between px-5 py-4 bg-white/90 backdrop-blur-xl border-b border-[#D4859B]/12">
        <button onClick={() => setMobileOpen(true)} aria-label="Menu" className="w-9 h-9 rounded-full hover:bg-seikat-pink/10 flex items-center justify-center text-[#1C1A19]" data-testid="admin-mobile-menu">
          <Menu size={18} />
        </button>
        <NavLink to="/" className="font-heading text-xl text-[#1C1A19]">Seikat<span className="text-seikat-pink">.</span></NavLink>
        <div className="text-[10px] tracking-widest uppercase text-seikat-pink">Workspace</div>
      </header>

      <main className="flex-1 lg:ml-64 min-h-screen px-6 lg:px-10 pt-20 lg:pt-10 pb-24" data-testid="admin-main">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2 text-xs tracking-widest uppercase font-body">
            <span className="text-[#A8A29A]">Admin</span>
            <span className="text-[#A8A29A]/50">/</span>
            <span className="text-seikat-pink capitalize">{currentLabel()}</span>
          </div>
          <div className="flex items-center gap-2">
            <AdminNotificationBell />
            <NavLink to="/" data-testid="admin-view-site" className="btn-pill text-[10px] tracking-[0.2em]">
              View Site <ArrowUpRight size={12} />
            </NavLink>
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
